<template>
  <div class="od-page">
    <div class="od-body">
      <div class="od-top od-top--spaced">
        <button type="button" class="od-back" aria-label="返回" @click="goBack">
          <img src="@/assets/images/svg/back.svg" alt="" />
        </button>
        <h1 class="od-title">我的订单</h1>
        <div class="od-toolbar">
          <el-input
            v-model="keyWord"
            class="order-search__input"
            placeholder="搜索订单号或商品"
            clearable
            size="large"
            @keyup.enter="handleSearch"
          />
          <button type="button" class="order-search__btn" @click="handleSearch">
            搜索
          </button>
        </div>
      </div>

      <div class="order-tabs-wrap">
        <el-tabs
          v-model="avtiveType"
          class="order-tabs"
          @tab-change="handleTabChange"
        >
          <el-tab-pane
            v-for="item in allType"
            :key="item.id"
            :label="item.name"
            :name="item.id"
          />
        </el-tabs>
      </div>

      <div class="order-list-section">
        <el-skeleton
          :loading="loading && orderList.length === 0"
          animated
          :rows="5"
        >
          <template #default>
            <el-empty
              v-if="!loading && orderList.length === 0"
              class="order-empty"
              description="暂无订单"
            />
            <ul
              v-else
              v-infinite-scroll="load"
              :infinite-scroll-disabled="!hasMore || loading"
              :infinite-scroll-distance="80"
              class="order-list"
            >
              <li
                v-for="item in orderList"
                :key="item.id"
                class="order-card"
                role="button"
                tabindex="0"
                @click="openDetail(item)"
                @keyup.enter="openDetail(item)"
              >
                <div class="order-card__head">
                  <div class="order-card__head-left">
                    <span class="order-card__label">订单编号</span>
                    <span class="order-card__id">{{
                      item.orderNo || item.id
                    }}</span>
                  </div>
                  <span
                    class="order-status"
                    :class="statusClass(item.status)"
                    >{{ statusText(item.status) }}</span
                  >
                </div>
                <div class="order-card__body">
                  <div
                    v-for="(line, index) in item.items || []"
                    :key="index"
                    class="order-line"
                  >
                    <div class="order-line__thumb">
                      <img :src="line.goodsImage" alt="" />
                    </div>
                    <div class="order-line__meta">
                      <div class="order-line__name">{{ line.goodsName }}</div>
                      <div class="order-line__sub">
                        单价 ¥{{ formatMoney(line.skuAmount) }} · ×{{
                          line.quantity
                        }}
                      </div>
                    </div>
                    <div class="order-line__price">
                      ¥{{ formatMoney(line.payAmount) }}
                    </div>
                  </div>
                </div>
                <div class="order-card__foot">
                  <div class="order-card__foot-left">
                    <span class="order-card__count"
                      >共 {{ item.items?.length || 0 }} 件</span
                    >
                    <span class="order-card__sep" aria-hidden="true">·</span>
                    <span class="order-card__total">
                      实付
                      <strong>¥{{ formatMoney(orderPayTotal(item)) }}</strong>
                    </span>
                  </div>
                  <span class="order-card__link">
                    详情
                    <span class="order-card__chev" aria-hidden="true">›</span>
                  </span>
                </div>
              </li>
            </ul>
          </template>
        </el-skeleton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { getOrderPage } from "@/api/shopping";

const router = useRouter();
const avtiveType = ref("ALL");
const keyWord = ref("");
const allType = ref([
  { id: "ALL", name: "全部" },
  { id: "TO_BE_PAID", name: "待付款" },
  { id: "TO_BE_SHIPPED", name: "待发货" },
  { id: "SHIPPING", name: "待收货" },
  { id: "FINISHED", name: "已完成" },
  { id: "CANCELED", name: "已取消" },
  { id: "REFUNDING", name: "退款中" },
  { id: "REFUNDED", name: "已退款" },
]);

const query = ref({ page: 1, size: 8 });
const orderList = ref([]);
const orderAllData = ref([]);
const loading = ref(false);
const hasMore = ref(true);

const STATUS_MAP = {
  FINISHED: "已完成",
  TO_BE_SHIPPED: "待发货",
  SHIPPING: "待收货",
  TO_BE_PAID: "待付款",
  CANCELED: "已取消",
  REFUNDING: "退款中",
  REFUNDED: "已退款",
};

function statusText(s) {
  return STATUS_MAP[s] || "—";
}

function statusClass(s) {
  const k = (s || "").toLowerCase().replace(/_/g, "-");
  return `order-status--${k}`;
}

/** 列表卡片底部「实付」：按行小计汇总 */
function orderPayTotal(row) {
  const lines = row?.items;
  if (!Array.isArray(lines) || lines.length === 0) return 0;
  return lines.reduce((sum, line) => {
    const n = Number(line?.payAmount);
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);
}

function resetList() {
  query.value.page = 1;
  orderList.value = [];
  hasMore.value = true;
}

const fetchData = () => {
  loading.value = true;
  const status = avtiveType.value === "ALL" ? undefined : avtiveType.value;
  getOrderPage(query.value, {
    status,
    keyword: keyWord.value || undefined,
  })
    .then((res) => {
      if (res.code === 200) {
        orderAllData.value = res.data;
        const list = res.data?.list || [];
        orderList.value = orderList.value.concat(list);
        if (list.length < query.value.size) {
          hasMore.value = false;
        }
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const load = () => {
  if (!hasMore.value || loading.value) return;
  query.value.page += 1;
  fetchData();
};

const formatMoney = (val) => {
  const n = Number(val);
  if (Number.isNaN(n)) return "0.00";
  return n.toFixed(2);
};

const openDetail = (item) => {
  router.push(`/myOrder/detail/${item.id}`);
};

const goBack = () => {
  router.push("/");
};

const handleTabChange = () => {
  resetList();
  fetchData();
};

const handleSearch = () => {
  resetList();
  fetchData();
};

onMounted(() => {
  resetList();
  fetchData();
});
</script>

<style scoped lang="scss">
@import "@/styles/order-flow.scss";

.order-search__input {
  flex: 1 1 auto;
  min-width: 120px;
  max-width: min(480px, 100%);

  :deep(.el-input__wrapper) {
    min-height: 46px;
    border-radius: var(--ios-radius-md, 14px);
    background: var(--ux-surface-muted, rgba(255, 255, 255, 0.08));
    box-shadow: none;
    border: 0.5px solid var(--ios-separator, rgba(255, 255, 255, 0.08));
    padding-left: 16px;
    padding-right: 12px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  :deep(.el-input__wrapper.is-focus) {
    border-color: rgba(0, 212, 255, 0.35);
    box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
  }

  :deep(.el-input__inner) {
    font-size: 15px;
    color: var(--ux-text-primary, #e6f0ff);
  }
}

.order-search__btn {
  flex-shrink: 0;
  min-width: 88px;
  height: 46px;
  padding: 0 20px;
  border: none;
  border-radius: var(--ios-radius-md, 14px);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #fff;
  background: linear-gradient(
    135deg,
    var(--ux-accent, #00d4ff) 0%,
    var(--ux-accent-2, #a78bfa) 100%
  );
  box-shadow: 0 0 16px var(--ux-accent-soft, rgba(0, 212, 255, 0.25));
  cursor: pointer;
  transition: transform 0.18s ease, filter 0.18s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    filter: brightness(1.05);
  }

  &:active {
    transform: scale(0.97);
    filter: brightness(0.96);
  }
}

.order-tabs-wrap {
  margin-bottom: 48px;
}

.order-tabs {
  :deep(.el-tabs__header) {
    margin: 0;
    border: none;
  }

  :deep(.el-tabs__nav-wrap)::after {
    display: none;
  }

  :deep(.el-tabs__nav-scroll) {
    overflow: visible;
  }

  :deep(.el-tabs__nav) {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 12px 24px;
    border: none;
  }

  /* 对齐 threeDspace .search_item */
  :deep(.el-tabs__item) {
    height: 46px;
    line-height: 46px;
    padding: 0 22px;
    min-width: 100px;
    border-radius: var(--ios-radius-md, 14px);
    font-size: 16px;
    font-weight: 400;
    color: var(--ux-text-primary, #e6f0ff);
    border: none;
    background: var(--ux-surface-muted, rgba(255, 255, 255, 0.08));
    flex-shrink: 0;
    transition: color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  }

  :deep(.el-tabs__item:hover) {
    background: var(--ios-fill-secondary, rgba(255, 255, 255, 0.12));
  }

  :deep(.el-tabs__item.is-active) {
    color: #fff;
    font-weight: 600;
    background: linear-gradient(
      135deg,
      var(--ux-accent, #00d4ff) 0%,
      var(--ux-accent-2, #a78bfa) 100%
    );
    box-shadow: 0 0 16px var(--ux-accent-soft, rgba(0, 212, 255, 0.35));
  }

  :deep(.el-tabs__active-bar) {
    display: none;
  }
}

.order-list-section {
  margin-top: 8px;
  min-height: 200px;
}

.order-empty {
  padding: 40px 0 48px;
  text-align: center;
}

.order-empty :deep(.el-empty__description) {
  font-size: 16px;
  color: var(--ux-text-secondary, #9fb4d6);
}

.order-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: min(72vh, 920px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.order-card {
  margin-bottom: 20px;
  border-radius: var(--ios-radius-lg, 18px);
  border: 0.5px solid var(--ux-glass-border, var(--ios-separator));
  box-shadow: var(--ux-glass-shadow, var(--ux-shadow-card));
  background: var(--ux-glass-bg, var(--ux-panel-bg));
  backdrop-filter: var(--ios-glass-blur-light, var(--ios-blur-light));
  -webkit-backdrop-filter: var(--ios-glass-blur-light, var(--ios-blur-light));
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s var(--ios-tap-ease, ease), box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: rgba(0, 212, 255, 0.2);
    box-shadow: var(--ux-glass-shadow-hover, var(--ux-shadow-card));
  }

  &:active {
    transform: scale(0.996);
  }
}

.order-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 18px 14px;
  border-bottom: 0.5px solid var(--ios-separator, rgba(255, 255, 255, 0.08));
}

.order-card__head-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.order-card__label {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ux-text-muted, rgba(159, 180, 214, 0.55));
}

.order-card__id {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--ux-text-primary, #e6f0ff);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-status {
  flex-shrink: 0;
  padding: 6px 14px;
  border-radius: var(--ios-pill, 980px);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.order-status--finished,
.order-status--canceled,
.order-status--refunded {
  color: rgba(230, 242, 255, 0.85);
  background: rgba(255, 255, 255, 0.12);
}

.order-status--to-be-paid {
  color: #0a1628;
  background: linear-gradient(
    180deg,
    rgba(255, 193, 94, 0.95) 0%,
    rgba(245, 158, 11, 0.95) 100%
  );
}

.order-status--to-be-shipped,
.order-status--refunding {
  color: #0a1628;
  background: linear-gradient(
    180deg,
    rgba(125, 211, 252, 0.95) 0%,
    rgba(56, 189, 248, 0.92) 100%
  );
}

.order-status--shipping {
  color: #0a1628;
  background: linear-gradient(
    180deg,
    rgba(167, 243, 208, 0.95) 0%,
    rgba(74, 222, 128, 0.9) 100%
  );
}

.order-card__body {
  padding: 2px 0 0;
}

.order-line {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  border-bottom: 0.5px solid var(--ios-separator, rgba(255, 255, 255, 0.06));

  &:last-child {
    border-bottom: none;
  }
}

.order-line__thumb {
  width: 64px;
  height: 64px;
  border-radius: var(--ios-radius-md, 14px);
  overflow: hidden;
  flex-shrink: 0;
  background: var(--ux-surface-muted, rgba(255, 255, 255, 0.06));

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.order-line__meta {
  flex: 1;
  min-width: 0;
}

.order-line__name {
  font-size: 18px;
  font-weight: 600;
  color: var(--ux-text-primary, #e6f0ff);
  letter-spacing: -0.02em;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.order-line__sub {
  margin-top: 6px;
  font-size: 15px;
  color: var(--ux-text-secondary, #9fb4d6);
}

.order-line__price {
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ux-text-primary, #e6f0ff);
}

.order-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px 16px;
  background: var(--ux-panel-bg, rgba(255, 255, 255, 0.04));
  border-top: 0.5px solid var(--ios-separator, rgba(255, 255, 255, 0.08));
  font-size: 15px;
  font-weight: 500;
  color: var(--ux-text-secondary, #9fb4d6);
}

.order-card__foot-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.order-card__count {
  color: var(--ux-text-muted, rgba(159, 180, 214, 0.75));
}

.order-card__sep {
  color: rgba(255, 255, 255, 0.2);
}

.order-card__total {
  color: var(--ux-text-secondary, #9fb4d6);

  strong {
    margin-left: 4px;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--ux-text-primary, #e6f0ff);
  }
}

.order-card__link {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 16px;
  font-weight: 600;
  color: var(--ux-accent, #5ee7ff);
}

.order-card__chev {
  font-size: 20px;
  font-weight: 300;
  color: var(--ux-accent-2, #c4b5fd);
  line-height: 1;
}

@media (max-width: 768px) {
  .order-search__input {
    max-width: none;
  }

  .order-tabs-wrap {
    margin-bottom: 40px;
  }

  .order-tabs :deep(.el-tabs__nav) {
    gap: 10px 12px;
  }

  .order-tabs :deep(.el-tabs__item) {
    min-width: 0;
    padding: 0 14px;
    font-size: 14px;
  }
}

@media (min-width: 900px) {
  .order-line__thumb {
    width: 72px;
    height: 72px;
  }

  .order-line__name {
    font-size: 19px;
  }

  .order-line__price {
    font-size: 19px;
  }
}
</style>
